import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, CornerDownLeft, FolderPlus, UserPlus, Timer, CalendarPlus, MailPlus,
  LayoutDashboard, FolderOpen, Users, BookUser, Calendar, Clock, ListTodo,
  FileText, Receipt, BookTemplate, UsersRound, TrendingUp, BarChart3,
  MessageSquare, CalendarClock, FileSearch, UserCheck,
} from 'lucide-react';
import { dossiersAPI, clientsAPI, contratsAPI, tempsAPI } from '../lib/api';
import { toast } from '../lib/toast';
import { useAuth } from '../context/AuthContext';
import { isAssocie } from '../lib/permissions';

const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * CommandPalette — lanceur universel (⌘K / Ctrl+K).
 * Recherche unifiée (dossiers, clients, contrats), navigation, et actions de
 * création rapide. Navigation clavier complète (↑ ↓ ↵ Échap). Theme-aware.
 * Monté une fois dans AppLayout. S'ouvre aussi via l'événement `adalia:command-open`.
 */
export default function CommandPalette() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canMgmt = isAssocie(user?.role);   // accès finance/admin (associés uniquement)

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const [data, setData] = useState({ dossiers: [], clients: [], contrats: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);
  const go = useCallback((path, state) => {
    setOpen(false);
    navigate(path, state ? { state } : undefined);
  }, [navigate]);

  // Ouverture : ⌘K / Ctrl+K + événement custom (depuis le header)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('adalia:command-open', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('adalia:command-open', onOpen);
    };
  }, []);

  // À l'ouverture : reset + focus + chargement des données (une fois par ouverture)
  useEffect(() => {
    if (!open) return;
    setQ('');
    setSel(0);
    const t = setTimeout(() => inputRef.current?.focus(), 40);
    setLoading(true);
    Promise.all([dossiersAPI.list(), clientsAPI.list(), contratsAPI.list()])
      .then(([d, c, k]) => setData({ dossiers: d.data || [], clients: c.data || [], contrats: k.data || [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => clearTimeout(t);
  }, [open]);

  // Actions de création rapide (ouvrent la modale de la page cible via location.state)
  const actions = useMemo(() => [
    { label: 'Nouveau dossier', icon: FolderPlus, run: () => go('/dossiers', { quickCreate: true }) },
    { label: 'Nouveau client', icon: UserPlus, run: () => go('/clients', { quickCreate: true }) },
    { label: 'Saisir du temps', icon: Timer, run: () => go('/temps', { quickCreate: true }) },
    { label: 'Nouvelle échéance', icon: CalendarPlus, run: () => go('/echeances', { quickCreate: true }) },
    { label: 'Nouveau courrier', icon: MailPlus, run: () => go('/courriers', { quickCreate: true }) },
  ], [go]);

  const navItems = useMemo(() => {
    const base = [
      { label: 'Tableau de bord', icon: LayoutDashboard, path: '/' },
      { label: 'Dossiers', icon: FolderOpen, path: '/dossiers' },
      { label: 'Recherche documentaire', icon: FileSearch, path: '/recherche-documents' },
      { label: 'Clients', icon: Users, path: '/clients' },
      { label: 'Contacts', icon: BookUser, path: '/contacts' },
      { label: 'Calendrier', icon: Calendar, path: '/calendrier' },
      { label: 'Échéances', icon: Clock, path: '/echeances' },
      { label: 'Tâches', icon: ListTodo, path: '/taches' },
      { label: 'Suivi du temps', icon: Timer, path: '/temps' },
      { label: 'Contrats', icon: FileText, path: '/contrats' },
      { label: 'Modèles OHADA', icon: BookTemplate, path: '/modeles' },
      { label: 'Courriers', icon: MessageSquare, path: '/courriers' },
      { label: 'Équipe', icon: UsersRound, path: '/equipe' },
      { label: 'Maître Adalia', icon: MessageSquare, path: '/chat' },
    ];
    const mgmt = [
      { label: 'Facturation', icon: Receipt, path: '/facturation' },
      { label: 'Accès clients', icon: UserCheck, path: '/portail-admin' },
      { label: 'Pilotage financier', icon: TrendingUp, path: '/finance' },
      { label: 'Rapports', icon: BarChart3, path: '/rapports' },
      { label: 'Catalogue délais', icon: CalendarClock, path: '/catalogue-delais' },
    ];
    return canMgmt ? [...base, ...mgmt] : base;
  }, [canMgmt]);

  // Construction des groupes selon la requête
  const groups = useMemo(() => {
    const nq = norm(q);
    const m = (label) => !nq || norm(label).includes(nq);
    const g = [];

    // ── Le verbe TEMPS (hérité de l'exploration « Greffe », validé) ─────
    // « 2h30 kaba conclusions » ou « 45m nimba rdv » → saisie directe,
    // sans formulaire. Entrée = enregistré. Les rapides gagnent ;
    // les autres ne voient rien changer.
    const verbe = nq.match(/^(\d+)\s*h\s*(\d+)?\s+(\S+)\s*(.*)$/) || nq.match(/^(\d+)\s*m(?:in)?\s+(\S+)\s*(.*)$/);
    if (verbe) {
      const enHeures = verbe.length === 5;   // regex heures = 4 groupes, minutes = 3
      const h = enHeures ? +verbe[1] : 0;
      const min = enHeures ? +(verbe[2] || 0) : +verbe[1];
      const cible = enHeures ? verbe[3] : verbe[2];
      const note = ((enHeures ? verbe[4] : verbe[3]) || '').trim() || 'Diligences';
      const total = h * 60 + min;
      const duree = (h ? `${h} h` : '') + (h && min ? ' ' : '') + (min ? (h ? String(min).padStart(2, '0') : `${min} min`) : '');
      if (total > 0) {
        const fd = data.dossiers.filter((d) => norm(`${d.intitule} ${d.numero_dossier} ${d.client_nom}`).includes(cible)).slice(0, 5);
        if (fd.length) {
          g.push({
            title: 'Consigner du temps', items: fd.map((d) => ({
              label: `${duree} — ${d.intitule || d.numero_dossier}`,
              sub: `« ${note} » · facturable · Entrée pour enregistrer`,
              icon: Timer,
              run: async () => {
                try {
                  await tempsAPI.create({ duree_minutes: total, facturable: true, dossier_id: d.id, description: note });
                  toast.success(`Temps consigné · ${duree} sur ${d.numero_dossier || d.intitule}`);
                  close();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Échec de la saisie de temps');
                }
              },
            })),
          });
          return g;   // intention claire : on ne pollue pas avec la navigation
        }
      }
    }

    const fActions = actions.filter((a) => m(a.label));
    if (fActions.length) g.push({ title: 'Actions', items: fActions.map((a) => ({ label: a.label, icon: a.icon, run: a.run })) });

    const fNav = navItems.filter((n) => m(n.label));
    if (fNav.length) g.push({ title: 'Aller à', items: fNav.map((n) => ({ label: n.label, icon: n.icon, run: () => go(n.path) })) });

    if (nq) {
      const fd = data.dossiers.filter((d) => m(d.intitule) || m(d.numero_dossier) || m(d.client_nom)).slice(0, 6);
      if (fd.length) g.push({ title: 'Dossiers', items: fd.map((d) => ({ label: d.intitule || 'Sans intitulé', sub: [d.numero_dossier, d.client_nom].filter(Boolean).join(' · '), icon: FileText, run: () => go(`/dossiers/${d.id}`) })) });

      const fc = data.clients.filter((c) => m(c.nom_client) || m(c.email)).slice(0, 6);
      if (fc.length) g.push({ title: 'Clients', items: fc.map((c) => ({ label: c.nom_client, sub: c.email || c.telephone || '', icon: Users, run: () => go(`/clients/${c.id}`) })) });

      const fk = data.contrats.filter((c) => m(c.reference) || m(c.description) || m(c.client_nom)).slice(0, 6);
      if (fk.length) g.push({ title: 'Contrats', items: fk.map((c) => ({ label: c.description || c.reference, sub: [c.reference, c.client_nom].filter(Boolean).join(' · '), icon: Receipt, run: () => go(`/contrats/${c.id}`) })) });
    }
    return g;
  }, [q, actions, navItems, data, go, close]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  useEffect(() => { setSel((s) => Math.min(s, Math.max(0, flat.length - 1))); }, [flat.length]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => (flat.length ? (s + 1) % flat.length : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => (flat.length ? (s - 1 + flat.length) % flat.length : 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); flat[sel]?.run?.(); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
  };

  // Scroll de l'item sélectionné dans la vue
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-cmd-index="${sel}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  let runningIndex = -1;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[12vh]"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onMouseDown={close}
          aria-hidden={!open}
        >
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }} />
          <motion.div
            role="dialog" aria-label="Palette de commandes"
            className="relative w-full max-w-[600px] rounded-[14px] overflow-hidden"
            style={{ background: 'var(--widget-bg)', border: '1px solid var(--border-soft)', boxShadow: '0 24px 70px rgba(0,0,0,0.45)' }}
            initial={{ opacity: 0, y: -8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.22, 0.61, 0.36, 1] }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Champ */}
            <div className="flex items-center gap-3 px-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
              <Search className="w-4 h-4 shrink-0" style={{ color: 'var(--text-tertiary)' }} />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => { setQ(e.target.value); setSel(0); }}
                onKeyDown={onKeyDown}
                placeholder="Rechercher… ou « 2h30 kaba conclusions » pour consigner du temps"
                className="flex-1 bg-transparent outline-none py-3.5 text-[14px]"
                style={{ color: 'var(--text-primary)' }}
                data-testid="command-palette-input"
              />
              <kbd className="text-[10px] px-1.5 py-0.5 rounded" style={{ color: 'var(--text-tertiary)', border: '1px solid var(--border-color)' }}>Échap</kbd>
            </div>

            {/* Résultats */}
            <div ref={listRef} className="max-h-[56vh] overflow-y-auto py-2">
              {flat.length === 0 ? (
                <div className="px-4 py-10 text-center text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                  {loading ? 'Chargement…' : 'Aucun résultat'}
                </div>
              ) : (
                groups.map((grp, gi) => (
                  <div key={gi} className="mb-1">
                    <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-tertiary)' }}>{grp.title}</div>
                    {grp.items.map((it) => {
                      runningIndex += 1;
                      const idx = runningIndex;
                      const active = idx === sel;
                      const Icon = it.icon;
                      return (
                        <button
                          key={idx}
                          data-cmd-index={idx}
                          onMouseEnter={() => setSel(idx)}
                          onClick={() => it.run?.()}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
                          style={{ background: active ? 'rgba(201,162,39,0.12)' : 'transparent' }}
                        >
                          {Icon && <Icon className="w-4 h-4 shrink-0" style={{ color: active ? '#C9A227' : 'var(--text-tertiary)' }} />}
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] truncate" style={{ color: 'var(--text-primary)' }}>{it.label}</div>
                            {it.sub && <div className="text-[11px] truncate" style={{ color: 'var(--text-tertiary)' }}>{it.sub}</div>}
                          </div>
                          {active && <CornerDownLeft className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--text-tertiary)' }} />}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {/* Pied */}
            <div className="flex items-center gap-4 px-4 py-2 text-[10.5px]" style={{ borderTop: '1px solid var(--border-color)', color: 'var(--text-tertiary)' }}>
              <span className="flex items-center gap-1"><kbd className="px-1 rounded" style={{ border: '1px solid var(--border-color)' }}>↑</kbd><kbd className="px-1 rounded" style={{ border: '1px solid var(--border-color)' }}>↓</kbd> naviguer</span>
              <span className="flex items-center gap-1"><kbd className="px-1 rounded" style={{ border: '1px solid var(--border-color)' }}>↵</kbd> ouvrir</span>
              <span className="ml-auto">Adalia · ⌘K</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
